import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getThumbnailUrl } from '@/lib/thumbnail';
import ViewOptions from '@/components/ViewOptions';
import BackToAlbums from '@/components/BackToAlbums';

type Photo = {
	id: number;
	src: string;
	alt: string;
};

type PhotoGridProps = {
	photos: Photo[];
};

type PhotoItemProps = {
	photo: Photo;
	albumId: string;
	view: 'grid' | 'masonry';
	aspect: 'square' | 'vertical' | 'horizontal';
};

const PhotoItem: React.FC<PhotoItemProps> = ({ photo, albumId, view, aspect }) => {
	const [loaded, setLoaded] = useState(false);

	return (
		<Link
			to={`/albums/${albumId}/photos/${photo.id}`}
			className="group relative block overflow-hidden transition duration-200 hover:opacity-80 break-inside-avoid mb-4"
		>
			<div
				className={`${view === 'grid'
					? aspect === 'square'
						? 'aspect-square'
						: aspect === 'vertical'
							? 'aspect-[2/3]'
							: 'aspect-[3/2]'
					: 'w-full h-auto'
					} relative overflow-hidden bg-gray-100`}
			>
				{/* Skeleton Loader */}
				<div className={`skeleton ${loaded ? 'hidden-skeleton' : ''}`} />

				{/* Image */}
				<img
					src={getThumbnailUrl(photo)}
					alt={photo.alt}
					loading="lazy"
					className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'
						}`}
					onLoad={() => setLoaded(true)}
				/>
			</div>
		</Link>
	);
};

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos = [] }) => {
	const location = useLocation();
	const albumId = location.pathname.split('/')[2]; // Extracts albumId from the URL path
	const [view, setView] = useState<'grid' | 'masonry'>('masonry');
	const [aspect, setAspect] = useState<'square' | 'vertical' | 'horizontal'>('square');

	return (
		<div className="space-y-6">
			{/* View Options with Back Button */}
			<ViewOptions
				currentView={view}
				onChangeView={setView}
				currentAspect={aspect}
				onChangeAspect={setAspect}
				backButton={<BackToAlbums />}
			/>

			{/* Photo Grid */}
			<section
				className={`${view === 'grid'
					? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'
					: view === 'masonry'
						? 'columns-2 sm:columns-3 md:columns-4 gap-4'
						: 'flex flex-col gap-8'
					}`}
			>
				{photos.map((photo) => (
					<PhotoItem
						key={photo.id}
						photo={photo}
						albumId={albumId}
						view={view}
						aspect={aspect}
					/>
				))}
			</section>
		</div>
	);
};

export default PhotoGrid;
